import type { ScenarioCalculations } from "../calculationTypes";
import type { ScenarioCalcContext } from "./context";

// Assembles the public ScenarioCalculations object from the calc context.
export const buildScenarioCalculations = (
  ctx: ScenarioCalcContext
): ScenarioCalculations => {
  return {
    waterCutRatio: ctx.waterCutRatio,
    tempGrad: ctx.tempGrad,
    orderedIntakes: ctx.orderedIntakes,
    descendingOrderPumps: ctx.descendingOrderPumps,
    ascendingOrderPumps: ctx.ascendingOrderPumps,
    primaryPump: ctx.primaryPump,
    bottomPump: ctx.bottomPump,
    primaryIntake: ctx.primaryIntake,
    bottomIntake: ctx.bottomIntake,
    intakesContainGasSeparator: ctx.intakesContainGasSeparator,
    primarySeal: ctx.primarySeal,
    bottomSeal: ctx.bottomSeal,
    orderedHousings: ctx.orderedHousings,
    descendingPumpsWithHousingIndex: ctx.descendingPumpsWithHousingIndex,
    orderedCables: ctx.orderedCables,
    descendingOrderSeals: ctx.descendingOrderSeals,
    productivityIndexStbdPsia: ctx.productivityIndexStbdPsia,
    liquidDensityAtDatumLbmft3: ctx.liquidDensityAtDatumLbmft3,
    yToolBypassTubingMaxODIn: ctx.yToolBypassTubingMaxODIn,
    orderedYToolBypassTubings: ctx.orderedYToolBypassTubings,
    orderedYToolEquipmentTubings: ctx.orderedYToolEquipmentTubings,
    equipmentLengths: {
      motorLengthFt: ctx.motorLengthFt,
      sensorLengthFt: ctx.sensorLengthFt,
      intakeLengthFt: ctx.intakeLengthFt,
      sealLengthFt: ctx.sealLengthFt,
      pumpLengthFt: ctx.pumpLengthFt,
      lengthForMleFt: ctx.lengthForMleFt,
      shroudLengthFt: ctx.shroudLengthFt,
      recommendedMleLengthFt: ctx.recommendedMleLengthFt,
      depthToReachMLEPlugFt: ctx.depthToReachMLEPlugFt,
      recommendedTotalCableLengthFt: ctx.recommendedTotalCableLengthFt,
      cableSurfaceLengthFt: ctx.cableSurfaceLengthFt,
      totalCableLengthFt: ctx.totalCableLengthFt,
      totalCableOnlyLengthFt: ctx.totalCableOnlyLengthFt,
      equipmentLengthSAGDFt: ctx.equipmentLengthSAGDFt,
      tubingLengthFt: ctx.tubingLengthFt,
      totalCasingLengthFt: ctx.totalCasingLengthFt,
      datumPointFt: ctx.datumPointFt,
      datumPointTVD: ctx.datumPointTVD,
      bottomOfYToolDepthFt: ctx.bottomOfYToolDepthFt,
      boltOnDischargeDepthFt: ctx.boltOnDischargeDepthFt,
      boltOnDischargeLengthFt: ctx.boltOnDischargeLengthFt,
      bottomOfDischargeDepthTVDFt: ctx.bottomOfDischargeDepthTVDFt,
      completePumpDepthFt: ctx.completePumpDepthFt,
      bottomOfPumpsDepthFt: ctx.bottomOfPumpsDepthFt,
      bottomOfPumpsTVDFt: ctx.bottomOfPumpsTVDFt,
      bottomOfMotorDepthFt: ctx.bottomOfMotorDepthFt,
      bottomOfMotorTVDFt: ctx.bottomOfMotorTVDFt,
      bottomOfSealsDepthFt: ctx.bottomOfSealsDepthFt,
      bottomOfSealsTVDFt: ctx.bottomOfSealsTVDFt,
      bottomOfEquipmentDepthFt: ctx.bottomOfEquipmentDepthFt,
      bottomOfEquipmentTVDFt: ctx.bottomOfEquipmentTVDFt,
      bottomOfIntakesDepthFt: ctx.bottomOfIntakesDepthFt,
      bottomOfIntakesTVDFt: ctx.bottomOfIntakesTVDFt,
      equipmentLengthFt: ctx.equipmentLengthFt,
      yToolEquipmentTubingLengthFt: ctx.yToolEquipmentTubingLengthFt,
      yToolBypassTubingLengthFt: ctx.yToolBypassTubingLengthFt,
      yToolBypassTubingDepthFt: ctx.yToolBypassTubingDepthFt
    },
    equipmentWeights: {
      topCasingWeightLbs: ctx.topCasingWeightLbs,
      bottomCasingWeightLbs: ctx.bottomCasingWeightLbs,
      totalCasingWeightLbs: ctx.totalCasingWeightLbs,
      motorWeightLbs: ctx.motorWeightLbs,
      boltOnDischargeWeightLbs: ctx.boltOnDischargeWeightLbs,
      sensorWeightLbs: ctx.sensorWeightLbs,
      intakeWeightLbs: ctx.intakeWeightLbs,
      sealWeightLbs: ctx.sealWeightLbs,
      pumpWeightLbs: ctx.pumpWeightLbs,
      cableWeightLbs: ctx.totalCablesWeightPounds,
      mleWeightLbs: ctx.mleWeightLbs,
      tubingWeightLbs: ctx.tubingWeightLbs,
      topTubingWeightLbs: ctx.topTubingWeightLbs,
      bottomTubingWeightLbs: ctx.bottomTubingWeightLbs,
      equipmentWeightLbs: ctx.equipmentWeightLbs,
      totalStringWeightLbs: ctx.totalStringWeightLbs
    },
    motorLeadExtensionCalculations: {
      mleCableData: ctx.mleCableData
    },
    surfaceDriveCalculations: {
      transformerTapData: ctx.transformerTapData,
      surfaceDriveMaxHz: ctx.surfaceDriveMaxHz,
      surfaceDriveMinHz: ctx.surfaceDriveMinHz,
      requiredVoltageAtMaxHzV: ctx.requiredVoltageAtMaxHzV,
      requiredVoltageAtMaxHz60HzV: ctx.requiredVoltageAtMaxHz60HzV,
      kvaAtMaxHz: ctx.kvaAtMaxHz,
      requiredVoltageV: ctx.requiredVoltageV,
      requiredKVA: ctx.requiredKVA,
      stepDownTransformerLoadRatio: ctx.stepDownTransformerLoadRatio,
      stepUpTransformerLoadRatio: ctx.stepUpTransformerLoadRatio,
      vsdLoadRatio: ctx.vsdLoadRatio,
      requiredKVA60Hz: ctx.requiredKVA60Hz,
      vsdOutputVoltageV: ctx.vsdOutputVoltageV,
      systemEmissionsCO2T: ctx.systemEmissionsCO2T,
      maxAmpRatingA: ctx.maxAmpRatingA,
      systemPowerKW: ctx.systemPowerKW,
      surfacePFRatio: ctx.surfacePFRatio,
      monthlySystemCostUSD: ctx.monthlySystemCostUSD,
      startupVoltage: ctx.startupVoltage,
      startUpVoltageNamplateRatio: ctx.startUpVoltageNamplateRatio,
      vsdKVARating: ctx.vsdKVARating,
      vfdLoadRatio: ctx.vfdLoadRatio,
      transformerRatio: ctx.transformerRatio,
      totalSystemEfficiencyRatio: ctx.totalSystemEfficiencyRatio,
      cableEfficiencyRatio: ctx.cableEfficiencyRatio,
      sealEfficiencyRatio: ctx.sealEfficiencyRatio,
      pumpEfficiencyRatio: ctx.pumpEfficiencyRatio,
      motorEfficiencyRatio: ctx.motorEfficiencyRatio,
      iLimitA: ctx.iLimitA,
      iLimitSync: ctx.iLimitSync,
      vsdUnderloadA: ctx.vsdUnderloadA,
      vsdOverloadA: ctx.vsdOverloadA
    },
    motorCalculations: {
      motorPoles: ctx.motorPoles,
      motorEnergyKWH: ctx.motorEnergyKWH,
      hpNameplate: ctx.hpNameplate,
      hpElevationDerated: ctx.hpElevationDerated,
      voltageNameplate: ctx.voltageNameplate,
      amperageNameplate: ctx.amperageNameplate,
      reratedNameplateHP: ctx.reratedNameplateHP,
      nameplate50HzHP: ctx.nameplate50HzHP,
      reratedNameplateV: ctx.reratedNameplateV,
      nameplate50HzV: ctx.nameplate50HzV,
      reratedNameplateA: ctx.reratedNameplateA,
      adjustedReratedNameplateA: ctx.adjustedReratedNameplateA,
      adjustedReratedNameplateV: ctx.adjustedReratedNameplateV,
      adjustedReratedNameplateHP: ctx.adjustedReratedNameplateHP,
      motorVoltageV: ctx.motorVoltageV,
      motorVoltage60HzV: ctx.motorVoltage60HzV,
      maxVSDOutputVoltage60HzV: ctx.maxVSDOutputVoltage60HzV,
      motorCurrentA: ctx.motorCurrentA,
      motorSkinTemperatureF: ctx.motorSkinTemperatureF,
      motorInternalTemperatureF: ctx.motorInternalTemperatureF,
      motorEfficiencyRatio: ctx.motorEfficiencyRatio,
      motorInputW: ctx.motorInputW,
      motorOutputW: ctx.motorOutputW,
      motorPowerFactor: ctx.motorPowerFactor,
      motorLoadRatio: ctx.motorLoadRatio,
      motorLoadRatioNP: ctx.motorLoadRatioNP,
      motorShaftPowerLimitHP: ctx.motorShaftPowerLimitHP,
      motorShaftRatingRatio: ctx.motorShaftRatingRatio,
      motorRatedRPM: ctx.motorRatedRPM,
      flowVelocityAroundMotorFTSEC: ctx.flowVelocityAroundMotorFTSEC,
      totalRequiredBrakeHP: ctx.totalRequiredBrakeHP,
      totalRequired60HzBrakeHP: ctx.totalRequired60HzBrakeHP,
      bestMotorInternalTempF: ctx.bestMotorInternalTempF,
      wellFlowingPressureAtMotorPSIA: ctx.wellFlowingPressureAtMotorPSIA,
      downholeMotorTemperatureF: ctx.downholeMotorTemperatureF,
      yToolBypassTubingAreaAtMotorFt2: ctx.yToolBypassTubingAreaAtMotorFt2,
      yToolBypassTubingODAtMotorInches: ctx.yToolBypassTubingODAtMotorInches,
      motorCrossSectionFt2: ctx.motorCrossSectionFt2,
      motorVoltageCorrections: ctx.motorVoltageCorrections
    },
    pumpCalculations: {
      individual: ctx.individualPumpCalculations,
      aggregate: {
        calculatedGasHeadRerateRatio: ctx.calculatedGasHeadRerateRatio,
        legacyGasHeadRerateRatio: ctx.legacyGasHeadRerateRatio,
        averageLiquidDensityLBMFT3: ctx.averageLiquidDensityLBMFT3,
        averageFluidDensityLBMFT3: ctx.averageFluidDensityLBMFT3,
        requiredPumpBrakeHP: ctx.requiredPumpBrakeHP,
        averageSpecificGravity: ctx.averageSpecificGravity,
        averageLiquidSpecificGravity: ctx.averageLiquidSpecificGravity,
        requiredTotalDynamicHeadFt: ctx.requiredTotalDynamicHeadFt,
        producedTotalDynamicHeadFt: ctx.producedTotalDynamicHeadFt,
        gasHeadRerateRatio: ctx.gasHeadRerateRatio,
        requiredDischargePressurePSIA: ctx.requiredDischargePressurePSIA,
        intakeFlowRateSTBD: ctx.intakeFlowRateSTBD,
        dischargeFlowRateSTBD: ctx.dischargeFlowRateSTBD,
        producedDischargePressurePSIA: ctx.producedDischargePressurePSIA,
        differentialPressurePSIA: ctx.differentialPressurePSIA,
        dischargeTemperatureF: ctx.dischargeTemperatureF,
        operatingRPM: ctx.operatingRPM,
        operatingThrustLoad: ctx.operatingThrustLoad,
        thrustLoadPounds: ctx.thrustLoadPounds,
        pumpEfficiencyRatio: ctx.pumpEfficiencyRatio,
        shutInPressureRisePSIA: ctx.shutInPressureRisePSIA,
        dischargeShutInBurstPressurePSIA: ctx.dischargeShutInBurstPressurePSIA,
        wellheadShutInBurstPressurePSIA: ctx.wellheadShutInBurstPressurePSIA,
        shutInThrustLoadPounds: ctx.shutInThrustLoadPounds,
        stdShutInBurstPercentage: ctx.stdShutInBurstPercentage,
        hsShutInBurstPercentage: ctx.hsShutInBurstPercentage,
        stdOperatingBurstPercentage: ctx.stdOperatingBurstPercentage,
        hsOperatingBurstPercentage: ctx.hsOperatingBurstPercentage,
        shaftPowerLimitStandardHP: ctx.shaftPowerLimitStandardHP,
        shaftPowerLimitHighStrengthHP: ctx.shaftPowerLimitHighStrengthHP,
        operatingBurstPressurePsia: ctx.operatingBurstPressurePsia,
        axialBoltLoadRatio: ctx.axialBoltLoadRatio,
        axialBoltLoadShutInRatio: ctx.axialBoltLoadShutInRatio,
        wellFlowingPressureSurfacePSIA: ctx.wellFlowingPressureSurfacePSIA,
        pumpTorqueFtLbs: ctx.pumpTorqueFtLbs,
        taperTotalOperatingHeadFeet: ctx.taperTotalOperatingHeadFeet,
        taperTotalMaxHeadFeet: ctx.taperTotalMaxHeadFeet,
        totalMaxHeadFeet: ctx.totalMaxHeadFeet,
        totalMaxBrakeHP: ctx.totalMaxBrakeHP,
        totalMaxPressurePSIA: ctx.totalMaxPressurePSIA,
        totalMaxDischargePressurePSIA: ctx.totalMaxDischargePressurePSIA
      }
    },
    cableCalculations: {
      individual: ctx.individualCableCalculations,
      aggregate: {
        totalImpedance: ctx.totalImpedance,
        totalCablesVoltLossAtNameplateAtMaxHzV:
          ctx.totalCablesVoltLossAtNameplateAtMaxHzV,
        totalCablesCostPerMonthUSD: ctx.totalCablesCostPerMonthUSD,
        totalCablesVoltLossV: ctx.totalCablesVoltLossV,
        totalCablesPowerLossKW: ctx.totalCablesPowerLossKW,
        totalCablesLengthFt: ctx.totalCablesLengthFt,
        totalCablesWeightPounds: ctx.totalCablesWeightPounds,
        totalCablesAverageTemperatureF: ctx.totalCablesAverageTemperatureF,
        downholeTotalImpedance: ctx.downholeTotalImpedance,
        downholeTotalCablesLengthFt: ctx.downholeTotalCablesLengthFt,
        downholeTotalCablesAverageTemperatureF:
          ctx.downholeTotalCablesAverageTemperatureF,
        downholeTotalCablesWeightPounds: ctx.downholeTotalCablesWeightPounds
      }
    },
    sealCalculations: {
      individual: ctx.individualSealCalculations,
      aggregate: {
        sealOperatingHP: ctx.sealOperatingHP,
        sealTotalVolumeGallons: ctx.sealTotalVolumeGallons,
        sealUpperChamberPullExpansionRatio:
          ctx.sealUpperChamberPullExpansionRatio,
        sealBearingLoadRatio: ctx.sealBearingLoadRatio,
        sealShutInBearingLoadRatio: ctx.sealShutInBearingLoadRatio,
        sealBearingRatingAt60HzLBS: ctx.sealBearingRatingAt60HzLBS,
        sealBearingRatingAtRPMLBS: ctx.sealBearingRatingAtRPMLBS,
        wellFlowingPressureAtSealPSIA: ctx.wellFlowingPressureAtSealPSIA,
        downholeSealTemperatureF: ctx.downholeSealTemperatureF
      }
    },
    intakeCalculations: {
      individual: ctx.individualIntakeCalculations,
      aggregate: {
        calculatedIntakeGasSeparationEfficiencyRatio:
          ctx.calculatedIntakeGasSeparationEfficiencyRatio,
        overrideIntakeGasSeparationEfficiencyRatio:
          ctx.overrideIntakeGasSeparationEfficiencyRatio,
        intakeGasSeparationEfficiencyRatio:
          ctx.intakeGasSeparationEfficiencyRatio,
        calculatedNaturalGasSeparationEfficiencyRatio:
          ctx.calculatedNaturalGasSeparationEfficiencyRatio,
        overrideNaturalGasSeparationEfficiencyRatio:
          ctx.overrideNaturalGasSeparationEfficiencyRatio,
        naturalGasSeparationEfficiencyRatio:
          ctx.naturalGasSeparationEfficiencyRatio,
        intakesContainNonBoltOn: ctx.intakesContainNonBoltOn,
        liquidAbovePumpTvdFt: ctx.liquidAbovePumpTvdFt,
        liquidAbovePumpMDFt: ctx.liquidAbovePumpMDFt,
        gasEnteringPumpRatio: ctx.gasEnteringPumpRatio,
        turpinCoefficient: ctx.turpinCoefficient,
        intakeHP: ctx.requiredIntakeBrakeHP
      }
    },
    fluidPropertiesAtReservoirContext: ctx.fluidPropertiesAtReservoirContext,
    fluidPropertiesInsideTubingContext: ctx.fluidPropertiesInsideTubingContext,
    fluidPropertiesAfterPumpDischargeTubingContext:
      ctx.fluidPropertiesAfterPumpDischargeTubingContext,
    fluidProperties: {
      gasSpecificGravity: ctx.gasSpecificGravity,
      fluidPropertiesAtDatum: ctx.fluidPropertiesAtDatum,
      fluidPropertiesAtReservoir: ctx.fluidPropertiesAtReservoir,
      fluidPropertiesAtPumpIntake: ctx.fluidPropertiesAtPumpIntake,
      fluidPropertiesAtIntake: ctx.fluidPropertiesAtIntake,
      fluidPropertiesAtWellPerfs: ctx.fluidPropertiesAtWellPerfs,
      fluidPropertiesAtMotor: ctx.fluidPropertiesAtMotor,
      fluidPropertiesAtSeals: ctx.fluidPropertiesAtSeals,
      fluidPropertiesAtSensor: ctx.fluidPropertiesAtSensor,
      hpsStageFluidProperties: ctx.hpsStageFluidProperties,
      fluidPropertiesAfterAllGasSeparationAtPump:
        ctx.fluidPropertiesAfterAllGasSeparationAtPump,
      oilPhaseGasSolubilityAtBubblePointSCFSTB:
        ctx.oilPhaseGasSolubilityAtBubblePointSCFSTB
    },
    hasDeviationSurvey: ctx.hasDeviationSurvey,
    selectedBubblePointPressurePSIA: ctx.selectedBubblePointPressurePSIA,
    selectedDewPointPressurePSIA: ctx.selectedDewPointPressurePSIA,
    selectedFluidViscosityCP: ctx.selectedFluidViscosityCP,
    selectedNPSHaFt: ctx.selectedNPSHaFt,
    customFluidProperties: ctx.customFluidProperties,
    customViscosityProperties: ctx.customViscosityProperties,
    entryRows: ctx.entryRows,
    entryRowsSurfaceToBoe: ctx.entryRowsSurfaceToBoe,
    entryRowsBotToBoe: ctx.entryRowsBotToBoe,
    entryRowsAtSeals: ctx.entryRowsAtSeals,
    topTubingFrictionProperties: ctx.topTubingFrictionProperties,
    bottomTubingFrictionProperties: ctx.bottomTubingFrictionProperties,
    basicData: ctx.basicData,
    surfaceTemperatureF: ctx.surfaceTemperatureF,
    bottomholeTemperatureF: ctx.bottomholeTemperatureF,
    surfaceFlowRates: ctx.desiredFlowRates,
    wellTestFlowRates: ctx.wellTestFlowRates,
    averageViscosityCP: ctx.averageViscosityCP,
    dischargeFluidDensityLBMFT3: ctx.dischargeFluidDensityLBMFT3,
    desiredLiquidRateSTDB: ctx.desiredLiquidRateSTDB,
    pumpIntakeTemperatureF: ctx.pumpIntakeTemperatureF,
    pumpAverageTemperatureF: ctx.pumpAverageTemperatureF,
    pumpHz: ctx.pumpHz,
    casingFlowRegime: ctx.casingFlowRegime,
    adjustedPumpHzFrequency: ctx.adjustedPumpHzFrequency,
    pumpIntakePressurePSIA: ctx.pumpIntakePressurePSIA,
    intakePressurePSIA: ctx.intakePressurePSIA,
    wellFlowingPressureAtSensorPSIA: ctx.wellFlowingPressureAtSensorPSIA,
    wellFlowingPressureAtDatumPSIA: ctx.wellFlowingPressureAtDatumPSIA,
    wellFlowingPressureAtPerfsPSIA: ctx.wellFlowingPressureAtPerfsPSIA,
    wellFlowingPressureSurfacePSIA: ctx.wellFlowingPressureSurfacePSIA,
    maxFlowRateBBLD: ctx.maxFlowRateBBLD,
    maxFlowRateAtIntakeBBLD: ctx.maxFlowRateAtIntakeBBLD,
    downholeSensorTemperatureF: ctx.downholeSensorTemperatureF,
    tubingFrictionLossPSIA: ctx.tubingFrictionLossPSIA,
    casingFrictionLossPSIA: ctx.casingFrictionLossPSIA,
    perfsAndDatumCasingFrictionPSIA: ctx.perfsAndDatumCasingFrictionPSIA,
    bottomOfEquipmentCasingFrictionPSIA:
      ctx.bottomOfEquipmentCasingFrictionPSIA,
    sensorAndMotorCasingFrictionPSIA: ctx.sensorAndMotorCasingFrictionPSIA,
    motorAndSealsCasingFrictionPSIA: ctx.motorAndSealsCasingFrictionPSIA,
    sealsAndIntakesCasingFrictionPSIA: ctx.sealsAndIntakesCasingFrictionPSIA,
    tubingInternalDiameterAtPumpDischargeIn:
      ctx.tubingInternalDiameterAtPumpDischargeIn,
    tubingOuterDiameterAtPumpDischargeIn:
      ctx.tubingOuterDiameterAtPumpDischargeIn,
    downholeWellPerfsTemperatureF: ctx.downholeWellPerfsTemperatureF,
    downholeMotorTemperatureF: ctx.downholeMotorTemperatureF,
    downholeTubingTemperatureF: ctx.downholeTubingTemperatureF,
    destructuredScenario: ctx.destructuredScenario,
    isScenarioEspShore: ctx.isScenarioEspShore,
    stageFluidProperties: ctx.stageFluidProperties
  };
};
